/**
 * Firebase Cloud Messaging (FCM) Utility
 *
 * Handles requesting notification permission, getting FCM tokens,
 * registering them with the backend for push notifications,
 * and listening for token refreshes.
 *
 * Supports both registered users (Firebase UID) and guest users (device ID).
 * The notification ID (FCM token) is always mapped to the user's identifier:
 *   - Registered users: mapped to their Firebase UID
 *   - Guest users: mapped to their device-level guest ID
 *
 * Token Lifecycle:
 *   1. On first grant: getToken() → register with backend
 *   2. On token refresh: onTokenRefresh() fires → re-register new token
 *   3. On 404 from FCM (invalid token): backend logs & cleans up stale token
 */
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { app } from '../firebase';
import api from './api';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

// Cache the service worker registration promise so we only register once
let swRegistrationPromise = null;

/**
 * Maximum number of retries for service worker activation and getToken().
 */
const MAX_RETRIES = 3;

/**
 * Delay helper for exponential backoff.
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise<void>}
 */
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Ensure the FCM service worker is registered and active.
 *
 * Uses retry logic with exponential backoff to handle a race condition where
 * Firebase's internal token-manager.ts calls PushManager.subscribe() before
 * the service worker is fully active from Firebase's perspective.
 *
 * The retry verifies that navigator.serviceWorker.getRegistration() returns
 * the active SW (mimicking what Firebase does internally), not just that our
 * registration.state is 'activated'.
 *
 * @param {number} retries - Number of retry attempts (default: MAX_RETRIES)
 * @returns {Promise<ServiceWorkerRegistration>}
 */
/**
 * Register the FCM service worker and cache the promise.
 * Extracted to avoid no-loop-func eslint warning.
 * @returns {Promise<ServiceWorkerRegistration>}
 */
function registerServiceWorker() {
  if (!swRegistrationPromise) {
    swRegistrationPromise = navigator.serviceWorker
      .register('/firebase-messaging-sw.js')
      .then((registration) => {
        console.log('📱 FCM Service Worker registered:', registration.scope);
        return registration;
      })
      .catch((err) => {
        // Reset so we can retry on next call
        swRegistrationPromise = null;
        throw err;
      });
  }
  return swRegistrationPromise;
}

async function ensureServiceWorker(retries = MAX_RETRIES) {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers not supported');
  }

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      // Register (or re-use cached registration promise)
      const registration = await registerServiceWorker();

      // If the service worker is already active, verify that Firebase can
      // find it via getRegistration() (which is what Firebase's internal
      // token-manager.ts does before calling PushManager.subscribe()).
      if (registration.active) {
        const swReg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
        if (swReg && swReg.active) {
          return registration;
        }
        console.log(
          `📱 SW active but getRegistration() returned no active SW. ` +
          `Retrying... (attempt ${attempt}/${retries})`
        );
      } else {
        // If there's a waiting worker (e.g., after an update), activate it
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        // Wait for the service worker to become active
        await new Promise((resolve, reject) => {
          const sw = registration.installing || registration.waiting;
          if (sw) {
            const onStateChange = () => {
              if (sw.state === 'activated') {
                sw.removeEventListener('statechange', onStateChange);
                resolve();
              } else if (sw.state === 'redundant') {
                sw.removeEventListener('statechange', onStateChange);
                reject(new Error('Service worker became redundant'));
              }
            };
            sw.addEventListener('statechange', onStateChange);
          } else {
            // No installing/waiting worker — resolve anyway
            resolve();
          }
        });
      }
    } catch (err) {
      console.warn(
        `📱 ensureServiceWorker attempt ${attempt}/${retries} failed:`,
        err.message
      );
      if (attempt === retries) {
        throw new Error(
          `Failed to ensure active service worker after ${retries} attempts: ${err.message}`
        );
      }
    }

    // Exponential backoff before retrying: 500ms, 1000ms, 2000ms
    if (attempt < retries) {
      await delay(500 * Math.pow(2, attempt - 1));
    }
  }
}

/**
 * Get an FCM token with retry logic.
 *
 * After ensureServiceWorker() succeeds, there can still be a brief window
 * where Firebase's internal state hasn't settled. This function:
 * 1. Adds a small safety delay before calling getToken()
 * 2. Retries getToken() up to MAX_RETRIES times with increasing delays
 *
 * @param {import('firebase/messaging').Messaging} messaging - Firebase Messaging instance
 * @param {object} options - Options passed to getToken()
 * @returns {Promise<string>} The FCM token
 */
async function getFcmTokenWithRetry(messaging, options) {
  // Small safety delay to let Firebase's internal state settle after SW activation
  await delay(100);

  let lastError = null;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const fcmToken = await getToken(messaging, options);
      if (fcmToken) {
        return fcmToken;
      }
      // getToken() returned null/empty — treat as failure
      lastError = new Error('getToken() returned null');
      console.warn(
        `📱 getToken attempt ${attempt}/${MAX_RETRIES} returned null. Retrying...`
      );
    } catch (err) {
      lastError = err;
      console.warn(
        `📱 getToken attempt ${attempt}/${MAX_RETRIES} failed:`,
        err.message
      );
      if (attempt === MAX_RETRIES) {
        throw err;
      }
    }

    // Wait before retrying: 1s, 2s
    if (attempt < MAX_RETRIES) {
      await delay(1000 * attempt);
    }
  }

  throw lastError || new Error('Failed to get FCM token after multiple attempts');
}

/**
 * Request notification permission and get the FCM token.
 * Registers the token with the backend.
 *
 * @param {string} userId - The user's ID to associate with the token
 *   (Firebase UID for registered users, device ID for guests)
 * @returns {Promise<string|null>} The FCM token, or null if permission denied
 */
export async function registerFcmToken(userId) {
  if (!app) {
    console.log('📱 Firebase not initialized. Skipping FCM registration.');
    return null;
  }

  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.log('📱 Notifications not supported in this browser.');
      return null;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('📱 Notification permission denied.');
      return null;
    }

    // Ensure the service worker is registered and active BEFORE calling getToken.
    // getToken() internally calls PushManager.subscribe() which requires an
    // active service worker. Without this, it throws:
    //   "Failed to execute 'subscribe' on 'PushManager': Subscription failed
    //    - no active Service Worker"
    //
    // The retry logic in ensureServiceWorker() handles the race condition where
    // Firebase's internal getRegistration() doesn't find the SW immediately.
    await ensureServiceWorker();

    // Get FCM token with retry logic
    const messaging = getMessaging(app);
    const fcmToken = await getFcmTokenWithRetry(messaging, {
      vapidKey: VAPID_KEY,
    });

    if (!fcmToken) {
      console.log('📱 Failed to get FCM token.');
      return null;
    }

    console.log('📱 FCM token obtained:', fcmToken.substring(0, 30) + '...');

    // Register with backend
    // The backend stores the FCM token keyed by userId (which could be
    // a Firebase UID for registered users or a device ID for guests).
    try {
      await api.registerFcmToken(userId, fcmToken);
      console.log('📱 FCM token registered with backend for user:', userId);
    } catch (registerErr) {
      // If backend registration fails (e.g., Firestore emulator 409),
      // log it but don't throw — the token was still obtained successfully
      // and the user can retry later.
      console.warn('📱 Backend FCM registration failed (token was obtained):', registerErr.message);
      // Still return the token since it was obtained successfully
    }

    // Listen for foreground messages
    onMessage(messaging, (payload) => {
      console.log('📱 Foreground message received:', payload);
      // The app can handle this via its own notification system
      // For now, we just log it. The service worker handles background messages.
    });

    return fcmToken;
  } catch (err) {
    console.error('📱 FCM registration failed:', err.message);
    // Log additional context for debugging
    console.error('📱 FCM registration context:', {
      userId,
      hasNotification: 'Notification' in window,
      notificationPermission: typeof Notification !== 'undefined' ? Notification.permission : 'N/A',
      hasServiceWorker: 'serviceWorker' in navigator,
      swRegistrationCached: !!swRegistrationPromise,
      vapidKeyConfigured: !!VAPID_KEY,
    });
    return null;
  }
}

/**
 * Check if push notifications are supported in this browser.
 */
export function isPushSupported() {
  return 'Notification' in window && 'serviceWorker' in navigator;
}
